import { safeMessage, tidy } from "../shared/safe";

// Pure parsing and wording for Template Studio (the library of complete-website kits, its synthetic demos and the choice of a template).
// Everything from the server is untrusted text. DEMO content is the kit's own synthetic site, served by the API under /preview/demo/…;
// it is never mixed with a client's approved content, and a client's missing details are never filled from it.

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const nonEmpty = (v: unknown): string | null => (typeof v === "string" && v.trim() !== "" ? v : null);
const int = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : null);
const strList = (v: unknown, max = 300): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string").map((x) => safeMessage(x, max)) : []);

export const FAMILY_LABEL: Record<string, string> = {
  profile: "Business Profile", blog: "Editorial Blog", directory: "Directory", wiki: "Wiki / Knowledge Base", service_hub: "Service and Location Guide", resource: "Magazine / Resource Hub", case_study: "Case Studies", newsroom: "Newsroom",
};
export const familyLabel = (family: string): string => (Object.hasOwn(FAMILY_LABEL, family) ? FAMILY_LABEL[family]! : tidy(family, 40));

export interface StudioPage { routeId: string; title: string; optional: boolean; collection: boolean }
export interface Supplied { key: string; description: string; required: boolean; example: unknown }
export interface Hosting { platform: string; maxFiles: number; minimumFiles: number; fits: boolean }
export interface DemoRef { title: string; pageCount: number; fileCount: number; previewPath: string; sitePath: string }
export interface StudioKit {
  kitId: string;
  version: string;
  family: string;
  name: string;
  summary: string;
  bestFor: string;
  look: string;
  composable: boolean;
  pages: StudioPage[];
  needs: string[];
  composesFrom: string[];
  supplied: Supplied | null;
  supports: string[];
  limitations: string[];
  demo: DemoRef | null;
  hosting: Hosting[];
}

/** Plain-words labels of what a kit needs. A collection with a minimum of zero, or a value that is not required, is marked optional. */
export function needLabels(rd: unknown): string[] {
  const out: string[] = [];
  for (const r of Array.isArray(rd) ? rd : []) {
    if (!isObject(r)) continue;
    const label = nonEmpty(r.label);
    if (!label) continue;
    const optional = r.required === false || (r.kind === "collection" && r.minItems === 0);
    out.push(optional ? `${tidy(label, 120)} (optional)` : tidy(label, 120));
  }
  return out;
}

/** A demo path is only ever one of the API's own /preview/demo/<kit>/<style>/ paths. */
const DEMO_PATH = /^\/preview\/demo\/[A-Za-z0-9_-]{1,64}\/(clean|explicit_index)\/(site\/[A-Za-z0-9_./-]*)?$/;
export const isDemoPath = (p: unknown): p is string => typeof p === "string" && DEMO_PATH.test(p) && !p.split("/").includes("..");

export function parseStudioKits(body: unknown): StudioKit[] | null {
  if (!isObject(body) || !Array.isArray(body.kits)) return null;
  return body.kits.filter(isObject).flatMap((k): StudioKit[] => {
    const kitId = nonEmpty(k.kitId);
    if (!kitId || !/^[A-Za-z0-9_-]{1,64}$/.test(kitId)) return [];
    const cat = isObject(k.catalogue) ? k.catalogue : {};
    const family = nonEmpty(k.family) ? tidy(k.family, 40) : "";
    const sup = isObject(cat.supplied) && nonEmpty(cat.supplied.key) ? { key: tidy(cat.supplied.key, 40), description: safeMessage(cat.supplied.description, 600), required: cat.supplied.required === true, example: cat.supplied.example } : null;
    const d = isObject(k.demo) && k.demo.available === true && isDemoPath(k.demo.previewPath) && isDemoPath(k.demo.sitePath)
      ? { title: nonEmpty(k.demo.title) ? tidy(k.demo.title, 160) : "Sample website", pageCount: int(k.demo.pageCount) ?? 0, fileCount: int(k.demo.fileCount) ?? 0, previewPath: k.demo.previewPath, sitePath: k.demo.sitePath }
      : null;
    return [{
      kitId,
      version: nonEmpty(k.version) ? tidy(k.version, 40) : "",
      family,
      name: nonEmpty(cat.name) ? tidy(cat.name, 80) : nonEmpty(k.title) ? tidy(k.title, 120) : kitId,
      summary: nonEmpty(cat.summary) ? safeMessage(cat.summary, 400) : "",
      bestFor: nonEmpty(cat.bestFor) ? safeMessage(cat.bestFor, 300) : "",
      look: nonEmpty(cat.look) ? safeMessage(cat.look, 300) : "",
      composable: k.composable === true,
      pages: (Array.isArray(k.pages) ? k.pages : []).filter(isObject).map((p) => ({ routeId: tidy(p.routeId, 60), title: tidy(p.title, 80), optional: p.optional === true, collection: p.kind === "collection" })),
      needs: needLabels(k.requiredData),
      composesFrom: strList(cat.composesFrom),
      supplied: sup,
      supports: strList(cat.supports),
      limitations: strList(cat.limitations, 500),
      demo: d,
      hosting: (Array.isArray(k.hosting) ? k.hosting : []).filter(isObject).flatMap((h): Hosting[] => (nonEmpty(h.platform) && int(h.maxFiles) !== null ? [{ platform: tidy(h.platform, 60), maxFiles: int(h.maxFiles)!, minimumFiles: int(h.minimumFiles) ?? 0, fits: h.fits === true }] : [])),
    }];
  });
}

export function filterKits(kits: readonly StudioKit[], search: string, family: string): StudioKit[] {
  const q = search.trim().toLowerCase();
  return kits.filter((k) => (family === "" || family === "all" || k.family === family) && (q === "" || `${k.name} ${k.summary} ${familyLabel(k.family)} ${k.pages.map((p) => p.title).join(" ")}`.toLowerCase().includes(q)));
}

/** The page types a card lists: fixed pages by name, collection pages as their type, optional ones marked. */
export function pageTypeLabels(kit: StudioKit): string[] {
  return kit.pages.map((p) => (p.optional ? `${p.title} (when supplied)` : p.title));
}

export function hostingNotes(kit: StudioKit): { fits: string[]; tooLarge: { platform: string; maxFiles: number }[] } {
  return { fits: kit.hosting.filter((h) => h.fits).map((h) => h.platform), tooLarge: kit.hosting.filter((h) => !h.fits).map((h) => ({ platform: h.platform, maxFiles: h.maxFiles })) };
}

export interface DemoDetail {
  kitId: string;
  title: string;
  pageCount: number;
  fileCount: number;
  previewPath: string;
  sitePath: string;
  pages: { pageId: string; title: string; group: string; url: string }[];
}
export function parseDemoDetail(body: unknown): DemoDetail | null {
  if (!isObject(body) || body.mode !== "demo" || body.synthetic !== true || !isDemoPath(body.previewPath) || !isDemoPath(body.sitePath)) return null;
  const pages = (Array.isArray(body.pages) ? body.pages : []).filter(isObject).flatMap((p) => (isDemoPath(p.url) && nonEmpty(p.pageId) ? [{ pageId: tidy(p.pageId, 80), title: tidy(p.title, 160), group: tidy(p.group, 60), url: p.url as string }] : []));
  return { kitId: tidy(body.kitId, 64), title: nonEmpty(body.title) ? tidy(body.title, 160) : "Sample website", pageCount: int(body.pageCount) ?? pages.length, fileCount: int(body.fileCount) ?? 0, previewPath: body.previewPath, sitePath: body.sitePath, pages };
}

/** Joins the API base and a validated demo path; null for anything that is not an http(s) address. */
export function demoSrc(apiBase: string, path: string): string | null {
  if (!isDemoPath(path)) return null;
  try {
    const u = new URL(path, apiBase.endsWith("/") ? apiBase : `${apiBase}/`);
    return u.protocol === "http:" || u.protocol === "https:" ? u.href : null;
  } catch {
    return null;
  }
}

/** Pretty JSON of a supplied-entries example, for the insert button. */
export function exampleText(s: Supplied): string {
  try { return JSON.stringify(s.example, null, 2); } catch { return "{}"; }
}
